// components/NavBar.tsx
import React, { useState, ReactNode, MouseEvent } from 'react';
import profil from '../../image/avatar.png';
import Modal from 'react-modal';
import '../../css/navbar.css';
import { useNavigate } from 'react-router-dom';
import { TiThMenu } from "react-icons/ti";
import { LuLogOut } from "react-icons/lu";
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import logo from '../../image/logo.png';
import ButtonCustom from '../../utils/custom-button';
import { FaLink } from "react-icons/fa";
import { useDispatch } from 'react-redux';
import { clearUser } from '../../userSlice';
import { useUserData } from '../../hooks/useUserData';
import { trackMenuClick } from '../../services/menuClick_service';
import { logoutUser } from '../../services/Login_service';

interface NavBarProps {
  menuContent: ReactNode;
}

const NavBar: React.FC<NavBarProps> = ({ menuContent }) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userName, userFirstname, profilePicture } = useUserData();
  const userStatus = localStorage.getItem("userStatus") || "anonymous";

  const resolveProfileImage = (path?: string | null) => {
    if (!path) return profil;

    // Enlever guillemets éventuels
    let cleaned = path.replace(/^"+|"+$/g, '');

    // Si URL absolue
    if (/^https?:\/\//i.test(cleaned)) return cleaned;

    const base =
      process.env.REACT_APP_API_BASE?.replace(/\/$/, '') ||
      'http://localhost:5200';

    // Normalisation du chemin
    cleaned = cleaned
      .replace(/\\/g, '/')       
      .replace(/\/+/g, '/')    
      .replace(/^\/+/, '')       
      .replace(/^storage\//, '');

    return `${base}/storage/${cleaned}`;
  };

  const profileImageSrc = resolveProfileImage(profilePicture);

  const openModal = () => setModalIsOpen(true);
  const closeModal = () => setModalIsOpen(false);

  const handleLogoutClick = () => {
    openModal();
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    
    setIsLoggingOut(true);
    
    try {
      closeModal();
      
      await trackMenuClick('logout', 'Déconnexion');
      
      await logoutUser();
      
      dispatch(clearUser());
      
      navigate('/login', { replace: true });
      
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      
      localStorage.clear();
      dispatch(clearUser());
      navigate('/login', { replace: true });
      
    } finally {
      setIsLoggingOut(false);
    }
  };

  const toggleMenu = () => setIsMenuOpen(v => !v);

  const renderMenuItems = () =>
    React.Children.map(menuContent, child => {
      if (!React.isValidElement(child)) return child;
      const element = child as React.ReactElement<any>;
      const originalOnClick = element.props.onClick as ((e: MouseEvent) => void) | undefined;

      return React.cloneElement(element, {
        onClick: (e: MouseEvent) => {
          originalOnClick?.(e);
          setIsMenuOpen(false);
        }
      });
    });

  return (
    <>
      <div className="navbar-logo-container">
        <div className="navbar-logo-wrapper">
          <img src={logo} alt="Logo" />
        </div>
      </div>

      <div className="navbar-header-content">
        <div className="navbar-mobile-menu-trigger" onClick={toggleMenu}>
          <TiThMenu className="navbar-mobile-menu-icon" />
        </div>

        <div className="navbar-header-left" id="navbar-legale">
          <h2 className="navbar-title">
            <a
              href="https://annonce-legale-officielle.fr/"
              target="_blank"
              rel="noopener noreferrer"
              className="navbar-link"
            >
              Annonce Légale Officielle
              <FaLink className="navbar-custom-icon" />
            </a>
          </h2>
        </div>

        <div className="navbar-header-right">
          <Modal
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            contentLabel="Confirmation de déconnexion"
            style={{
              overlay: { backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 },
              content: {
                top: '20%', 
                left: '50%', 
                right: 'auto', 
                bottom: 'auto',
                transform: 'translate(-50%,-50%)', 
                padding: 0, 
                borderRadius: 10,
                maxWidth: 400, 
                width: '90%'
              }
            }}
          >
            <div className="navbar-modal-close">
              <FontAwesomeIcon 
                icon={faTimes} 
                className="navbar-modal-close-icon" 
                onClick={closeModal} 
              />
            </div>
            <div className="navbar-modal-content">
              <p>Voulez-vous vraiment vous déconnecter&nbsp;?</p>
            </div>
            <div className="navbar-modal-buttons">
              <ButtonCustom 
                title={isLoggingOut ? "Déconnexion..." : "Se déconnecter"}
                color="#2A919D" 
                onClick={handleLogout}
                disabled={isLoggingOut}
              />
            </div>
          </Modal>

          <div className="navbar-user-section">
            <img
              src={profileImageSrc}
              alt="Profil utilisateur"
              className="navbar-profile-img"
              onError={(e) => {
                (e.target as HTMLImageElement).src = profil;
              }}
            />
            {userStatus !== "anonymous" && 
              <div className="navbar-username">{userFirstname} {userName}</div>
            }
            <button
              className={`navbar-logout-btn ${isLoggingOut ? 'navbar-logout-btn-disabled' : ''}`}
              onClick={handleLogoutClick}
              title="Se déconnecter"
              disabled={isLoggingOut}
            >
              <LuLogOut className="navbar-logout-icon" />
              <span className="navbar-logout-text">
                {isLoggingOut ? 'Déconnexion...' : 'Se déconnecter'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Menu mobile */}
      {isMenuOpen && (
        <div className="navbar-mobile-menu">
          {renderMenuItems()}
        </div>
      )}
    </>
  );
};

export default NavBar;